import { PropsWithChildren } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Theme, ThemeProvider, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { indigo } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import KampusBadge from 'components/shared/KampusBadge';
import Columns from 'components/shared/Layout/Columns';
import Rows from 'components/shared/Layout/Rows';
import config from 'libs/config';

export type AuthLayoutProps = {
  backgroundImageUrl?: string;
  theme?: Theme;
} & PropsWithChildren;

function AuthLayout({
  backgroundImageUrl = config.domain.assets.authBg,
  theme = config.domain.themes[1],
  children,
}: AuthLayoutProps) {
  return (
    <ThemeProvider theme={config.domain.themes[1]}>
      <Columns>
        <Rows
          sx={{
            flexShrink: 0,
            px: 12,
            justifyContent: 'center',
            position: 'relative',
            bgcolor: (theme) => theme.palette.secondary.main,
            width: '570px',
            height: '100vh',
            [theme.breakpoints.down('md')]: {
              width: '100%',
            },
          }}
        >
          <Box>
            <KampusBadge LogoSvg={config.domain.assets.logo} />
            <Columns
              sx={{
                mt: 1,
                flex: 1,
                alignItems: 'center',
              }}
            >
              {children}
            </Columns>
          </Box>
        </Rows>
        <Stack
          sx={{
            [theme.breakpoints.down('md')]: {
              display: 'none',
            },
            justifyContent: 'center',
            width: '100%',
            backgroundImage: `url("${backgroundImageUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {config.domain.isDefaultDomain && (
            <Stack
              sx={{
                // Aligns banner visually with form on the right
                mt: 9,
                // Avoids opacity being applied to text
                bgcolor: 'rgba(255,255,255,.3)',
                p: 8,
                backdropFilter: 'blur(12px)',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  maxWidth: 800,
                  minWidth: 500,
                }}
              >
                <Typography variant="h4" gutterBottom>
                  <strong>Welcome to Kampus! 🎉</strong>
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mt: 3,
                    '& a': {
                      textDecoration: 'none',
                      color: indigo[500],
                    },
                  }}
                >
                  <p>
                    Kampus centralizes KPIs, reporting, and workflows with
                    real-time tracking, geo heatmaps, and automated insights.
                    Gain total visibility and optimize faster across channels.
                  </p>
                  <p>
                    <a
                      target="_blank"
                      href="https://kortx.io/expertise/forms/kampus-demo/"
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                      }}
                      rel="noreferrer"
                    >
                      See how Kampus works—request a demo.&nbsp;{' '}
                      <ArrowForwardIcon />
                    </a>
                  </p>
                </Box>
              </Box>
            </Stack>
          )}
        </Stack>
      </Columns>
    </ThemeProvider>
  );
}

export default AuthLayout;
